




import {Component, Vue, Prop, Watch} from 'vue-property-decorator';
import {Chart} from 'highcharts-vue';
import {accelerometerColorScheme} from '@u/colorSchemes';
import {AxisBoundaries} from '@/components/widgets/base/bindForms/interfaces';

@Component({
  components: {
    highcharts: Chart,
  },
})
export default class AccelerometerSegmentDiagramBar extends Vue {
  //Границы диаграммы по оси X
  @Prop({required: false})
  axisBoundaries!: AxisBoundaries;

  @Prop({required: false})
  commonBoundaries!: AxisBoundaries;

  @Prop({required: false})
  unit!: string;

  //Ключ для обновления размеров диаграммы
  @Prop({required: false, type: Number, default: 0})
  resizedKey!: number;

  @Prop({required: false, type: Number, default: 0})
  currentValue!: number;

  @Watch('resizedKey')
  onResizedKeyChanged() {
    this.getChart().reflow();
  }

  viewRoundingAccuracy = 2;

  //Объект графика
  getChart() {
    return (this.$refs.chart as any).chart;
  }

  //Т.к. есть вероятность, что не будет указана граница errorBoundary,
  // то для избежания ошибки ищем максимальное среди границ
  getMaxBoundary() {
    return Math.max(...Object.values(this.commonBoundaries).map(b => b ?? 0));
  }

  //Объект конфигурации графика
  get chartOptions() {
    const series = [];
    series.push({
      type: 'bar',
      color: this.currentColor,
      data: [this.chartValue],
    });

    let dataLabelsFormat = '';
    if (this.currentValue && this.unit) {
      const unit = this.unit != '-' ? this.unit : '';
      if (this.currentValue > 10000)
        dataLabelsFormat = `${(this.currentValue / 1000).toFixed(
          this.viewRoundingAccuracy,
        )} тыс. ${unit}`;
      else dataLabelsFormat = `${this.currentValue.toFixed(this.viewRoundingAccuracy)} ${unit}`;
    }

    return {
      credits: {
        enabled: false,
      },
      chart: {
        type: 'bar',
        marginLeft: 0,
        marginRight: 0,
        spacingLeft: 0,
        spacingRight: 0,
        marginTop: 0,
        marginBottom: 0,
        spacingTop: 0,
        spacingBottom: 0,
        animation: false,
      },
      navigation: {
        buttonOptions: {
          enabled: false,
        },
      },
      plotOptions: {
        series: {
          groupPadding: 0,
          pointPadding: 0,
          borderWidth: 1,
          borderColor: '#000000',
        },
        bar: {
          enableMouseTracking: false,
          pointWidth: 32,
          animation: false,
          dataLabels: {
            enabled: true,
            inside: true,
            align: 'left',
            format: dataLabelsFormat,
            style: {
              fontSize: '14px',
              textOutline: '0px',
              color: 'black',
              fontWeight: 'normal',
            },
          },
        },
      },
      yAxis: {
        min: 0,
        max: this.getMaxBoundary(),
        gridLineWidth: 0,
        visible: false,
        endOnTick: false,
      },
      tooltip: {
        enabled: false,
      },
      title: {
        text: '',
      },
      legend: {
        enabled: false,
      },
      series,
    };
  }

  get currentColor() {
    if (
      this.axisBoundaries?.warningBoundary &&
      this.currentValue < this.axisBoundaries.warningBoundary
    )
      return accelerometerColorScheme.normalColor;
    if (
      this.axisBoundaries?.dangerBoundary &&
      this.currentValue < this.axisBoundaries.dangerBoundary
    )
      return accelerometerColorScheme.warningColor;
    return accelerometerColorScheme.dangerColor;
  }

  getWarningValue() {
    if (this.commonBoundaries?.warningBoundary && this.axisBoundaries.warningBoundary)
      return (
        (this.commonBoundaries.warningBoundary * this.currentValue) /
        this.axisBoundaries.warningBoundary
      );
    return null;
  }

  getDangerValue() {
    if (
      this.commonBoundaries?.warningBoundary &&
      this.commonBoundaries?.dangerBoundary &&
      this.axisBoundaries.dangerBoundary &&
      this.axisBoundaries.warningBoundary
    )
      return (
        this.commonBoundaries.warningBoundary +
        ((this.commonBoundaries.dangerBoundary - this.commonBoundaries.warningBoundary) *
          this.currentValue) /
          (this.axisBoundaries.dangerBoundary - this.axisBoundaries.warningBoundary)
      );
    return null;
  }

  getErrorValue() {
    if (
      this.commonBoundaries?.dangerBoundary &&
      this.commonBoundaries?.errorBoundary &&
      this.axisBoundaries?.errorBoundary &&
      this.axisBoundaries?.dangerBoundary
    )
      return (
        this.commonBoundaries.dangerBoundary +
        ((this.commonBoundaries.errorBoundary - this.commonBoundaries.dangerBoundary) *
          this.currentValue) /
          (this.axisBoundaries.errorBoundary - this.axisBoundaries.dangerBoundary)
      );
    return null;
  }

  get chartValue() {
    let value;
    if (
      this.axisBoundaries?.warningBoundary &&
      this.currentValue < this.axisBoundaries.warningBoundary
    )
      value = this.getWarningValue();
    else if (
      this.axisBoundaries?.dangerBoundary &&
      this.currentValue < this.axisBoundaries.dangerBoundary
    )
      value = this.getDangerValue();
    else value = this.getErrorValue();

    if (value && value < 0) return 0;
    else return value;
  }
}
